// src/screens/AddPlayer.tsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Check } from "lucide-react";
import { LogoLoader } from "../components/LogoLoader";
import { v4 as uuidv4 } from "uuid";
import i18n from "../i18n";
import { BackButton } from "../components/BackButton";


// mini hook i18n sans generics lourds
function useI18nSafe() {
  const [lng, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

type Player = {
  id?: string;
  firstName: string;
  lastName: string;
  number: number;
  position: string;
  photoUrl?: string;
};

export const AddPlayer = () => {
  const { t } = useI18nSafe();
  const { id: teamId } = useParams();
  const navigate = useNavigate();

  const [player, setPlayer] = useState({
    firstName: "",
    lastName: "",
    number: "",
    position: "",
    photoUrl: "",
  });

  const [teamName, setTeamName] = useState("");
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeam = async () => {
      if (!teamId) return;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: teamData, error } = await supabase
        .from("teams")
        .select("team_name, players")
        .eq("id", teamId)
        .single();

      if (error || !teamData) {
        alert(t("addplayer:load_team_error"));
        return;
      }

      setTeamName(teamData.team_name || t("myteam:default_team_name"));
      setPlayers((teamData.players || []) as Player[]);
      setLoading(false);
    };

    fetchTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const handleChange = (field: string, value: string) => {
    setPlayer((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !teamId) return;

    const firstName = player.firstName.trim();
    const lastName = player.lastName.trim();
    const position = player.position.trim();
    const number = Number(player.number) || 0;

    if (!firstName || !lastName || !position) {
      alert(t("addplayer:required_fields"));
      return;
    }

    const newPlayer: Player = {
      id: uuidv4(),
      firstName,
      lastName,
      number,
      position,
      photoUrl: player.photoUrl || undefined,
    };

    const updatedPlayers = [...players, newPlayer];

    const { error } = await supabase
      .from("teams")
      .update({ players: updatedPlayers })
      .eq("id", teamId);

    if (error) {
      alert(t("addplayer:add_error"));
    } else {
      setPlayers(updatedPlayers);
      alert(t("addplayer:add_success"));
      navigate("/myteam");
    }
  };

  if (loading) {
    return <LogoLoader />;
  }

  return (
    <section className="min-h-screen w-full p-6 mx-auto bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      {/* Header mobile sticky */}
      <div className="sticky top-0 z-50 w-full bg-gray-50/95 dark:bg-gray-950/95 backdrop-blur border-b border-gray-200 dark:border-gray-800 mb-6">
        <div className="relative mx-auto max-w-3xl px-4 py-3 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)]">
          {/* Bouton retour */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2">
            <BackButton />
          </div>

          {/* Titre centré */}
          <h1 className="text-lg font-bold text-orange-600 dark:text-orange-400 text-center">
            {t("addplayer:title_with_team", { team: teamName })}
          </h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto rounded-2xl border bg-white border-gray-200 shadow-sm p-6 dark:bg-gray-900 dark:border-gray-800">
        <div className="grid gap-4 mb-6">
          <input
            placeholder={t("addplayer:placeholder.first_name")}
            onChange={(e) => handleChange("firstName", e.target.value)}
            className="border rounded px-3 py-2 bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400/60 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
          />
          <input
            placeholder={t("addplayer:placeholder.last_name")}
            onChange={(e) => handleChange("lastName", e.target.value)}
            className="border rounded px-3 py-2 bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400/60 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
          />
          <input
            placeholder={t("addplayer:placeholder.number")}
            type="number"
            onChange={(e) => handleChange("number", e.target.value)}
            className="border rounded px-3 py-2 bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400/60 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
          />
          <input
            placeholder={t("addplayer:placeholder.position")}
            onChange={(e) => handleChange("position", e.target.value)}
            className="border rounded px-3 py-2 bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400/60 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
          />

          <button
            onClick={handleSave}
            className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-green-400/60 dark:bg-green-600 dark:hover:bg-green-700"
          >
            <Check className="w-4 h-4" />
            {t("addplayer:button_add")}
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto mt-8 rounded-2xl border bg-white border-gray-200 shadow-sm dark:bg-gray-900 dark:border-gray-800">
        <h2 className="text-xl font-semibold p-4 border-b border-gray-200 dark:border-gray-800">
          {t("addplayer:existing_players")}
        </h2>

        {players.length === 0 ? (
          <p className="p-4 text-gray-600 dark:text-gray-400">
            {t("addplayer:no_players_yet")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-100 dark:bg-gray-800">
                <tr className="text-gray-700 dark:text-gray-200">
                  <th className="p-3">{t("addplayer:table.num")}</th>
                  <th className="p-3">{t("addplayer:table.last_name")}</th>
                  <th className="p-3">{t("addplayer:table.first_name")}</th>
                  <th className="p-3">{t("addplayer:table.position")}</th>
                </tr>
              </thead>
              <tbody className="text-gray-800 dark:text-gray-200">
                {players.map((p, index) => (
                  <tr
                    key={p.id ?? index}
                    className="border-t border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                  >
                    <td className="p-3 font-bold">{p.number}</td>
                    <td className="p-3">{p.lastName}</td>
                    <td className="p-3">{p.firstName}</td>
                    <td className="p-3">{p.position}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};
